import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  makeStyles,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ReceiptIcon from '@material-ui/icons/Receipt';
import CloseIcon from '@material-ui/icons/Close';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import RefreshIcon from '@material-ui/icons/Refresh';
import RemoveIcon from '@material-ui/icons/Remove';
import SearchIcon from '@material-ui/icons/Search';
import {
  closestCorners,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import * as React from 'react';
import { FiscalService, TableOrderService } from 'services';
import { PaymentTypeLabel, PaymentType } from 'enums';
import { TableQrDialog } from './components';
import {
  BoardTable,
  PosMenuItem,
  TableOrder,
  TableOrderItem,
} from 'interfaces';

/* ------------------------------------------------------------------ helpers */

const RED = '#b23a2e';
const money = (n: number | string | undefined): string =>
  '€' + Number(n || 0).toFixed(2);

const isBar = (it: TableOrderItem): boolean => it.target === 'bar';
const foodItems = (o: TableOrder): TableOrderItem[] =>
  (o.tableOrderItems || []).filter((i) => !isBar(i) && i.status !== 'void');
const drinkItems = (o: TableOrder): TableOrderItem[] =>
  (o.tableOrderItems || []).filter((i) => isBar(i) && i.status !== 'void');
const courseNumbers = (o: TableOrder): number[] => {
  const set = new Set<number>();
  foodItems(o).forEach((i) => set.add(i.course || 1));
  return Array.from(set).sort((a, b) => a - b);
};
const orderTotal = (o: TableOrder): number =>
  (o.tableOrderItems || [])
    .filter((i) => i.status !== 'void')
    .reduce((s, i) => s + Number(i.unitPrice) * i.quantity, 0);
// Anything not yet sent to kitchen/bar: Course-1 food or drinks still 'ordered'.
const orderNeedsSend = (o: TableOrder): boolean =>
  (o.tableOrderItems || []).some(
    (i) => i.status === 'ordered' && (i.target === 'bar' || (i.course || 1) === 1),
  );

/* ------------------------------------------------------------------- styles */

const useStyles = makeStyles((theme) => ({
  root: { height: '100%', display: 'flex', flexDirection: 'column', background: '#efeae3' },
  full: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1400,
    background: '#efeae3',
  },
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    background: '#fff',
    borderBottom: '1px solid #e4dbcf',
  },
  title: { fontWeight: 800, fontSize: 18, color: '#231d17' },
  spacer: { flex: 1 },
  nav: { display: 'flex', gap: 4, background: '#f7f2eb', borderRadius: 999, padding: 3 },
  navBtn: { borderRadius: 999, minWidth: 0, padding: '6px 16px', fontWeight: 700, color: '#6e6357' },
  navOn: { background: RED, color: '#fff', '&:hover': { background: '#8f2d24' } },
  body: { flex: 1, display: 'flex', minHeight: 0 },
  floorWrap: { flex: 1, overflow: 'auto', padding: theme.spacing(3.5, 4) },
  panelWrap: {
    width: 400,
    flex: 'none',
    borderLeft: '1px solid #e4dbcf',
    background: '#f7f2eb',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  areaHead: {
    fontWeight: 800,
    textTransform: 'uppercase',
    color: '#6e6357',
    fontSize: 13,
    letterSpacing: 1,
    margin: theme.spacing(1, 0, 1.5),
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
    gridGap: 24,
    marginBottom: theme.spacing(4),
  },
  boardGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(4),
  },
  card: {
    position: 'relative',
    textAlign: 'left',
    width: 210,
    minHeight: 140,
    margin: 14,
    padding: 20,
    borderRadius: 16,
    border: '1px solid #e4dbcf',
    background: '#fff',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
  },
  cardSel: { boxShadow: '0 6px 20px rgba(35,29,23,.16)', borderColor: '#c9bfae', background: '#fbf9f5' },
  cardBand: { position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, borderRadius: '14px 0 0 14px' },
  tnum: { fontSize: 26, fontWeight: 800, fontVariantNumeric: 'tabular-nums' },
  tsub: { fontSize: 12, color: '#9a8e80', fontWeight: 600 },
  pill: {
    alignSelf: 'flex-start',
    marginTop: 10,
    fontSize: 12,
    fontWeight: 700,
    padding: '4px 12px',
    borderRadius: 999,
  },
  cardTot: { position: 'absolute', right: 12, bottom: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  lockIco: { position: 'absolute', left: 14, bottom: 12, fontSize: 12, color: RED, fontWeight: 700 },

  panel: { flex: 1, overflow: 'auto', padding: theme.spacing(2) },
  emptyPanel: { height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9a8e80', textAlign: 'center', padding: 24 },
  ohead: { display: 'flex', alignItems: 'flex-start', gap: 8 },
  otitle: { fontSize: 28, fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1 },
  osub: { fontSize: 12, color: '#9a8e80', fontWeight: 700 },
  modeChip: { marginLeft: 'auto', fontWeight: 800, height: 34, fontSize: 12 },
  lockBanner: { borderRadius: 10, padding: '10px 12px', margin: theme.spacing(1.5, 0, 0.5), fontSize: 12.5, fontWeight: 600 },
  course: { background: '#fff', border: '1px solid #e4dbcf', borderRadius: 14, marginTop: 12, overflow: 'hidden' },
  chead: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid #efe8dd' },
  cname: { fontWeight: 800, fontSize: 14 },
  cstat: { fontSize: 11, fontWeight: 800, textTransform: 'uppercase', padding: '3px 8px', borderRadius: 6 },
  fireBtn: { marginLeft: 'auto', background: RED, color: '#fff', fontWeight: 800, minHeight: 40, '&:hover': { background: '#8f2d24' } },
  itemRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px' },
  itemQty: { fontWeight: 700, color: RED, minWidth: 30, fontVariantNumeric: 'tabular-nums' },
  itemName: { flex: 1, fontSize: 15, fontWeight: 600 },
  itemNote: { display: 'block', fontSize: 12.5, color: '#d2542e', fontWeight: 600 },
  itemPrice: { fontWeight: 600, color: '#6e6357', fontVariantNumeric: 'tabular-nums' },
  totRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 20, paddingTop: 14, borderTop: '1px dashed #e4dbcf' },
  totAmt: { fontSize: 26, fontWeight: 800, fontVariantNumeric: 'tabular-nums' },
  actions: { display: 'flex', gap: 10, padding: theme.spacing(1.5, 2), borderTop: '1px solid #e4dbcf', background: '#f7f2eb' },
  addBtn: { flex: 1, minHeight: 52, fontWeight: 800, fontSize: 15, border: `1px solid ${RED}`, color: RED },
  payBtn: { flex: 1, minHeight: 52, fontWeight: 800, fontSize: 15, background: '#3c8b57', color: '#fff', '&:hover': { background: '#2f6e45' } },

  /* POS dialog */
  pos: { display: 'flex', height: '100%', background: '#fff' },
  posMenu: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, borderRight: '1px solid #e4dbcf' },
  posSearch: { padding: theme.spacing(2), borderBottom: '1px solid #efe8dd' },
  cats: { display: 'flex', gap: 8, marginTop: 12, overflowX: 'auto', paddingBottom: 4 },
  mgrid: { flex: 1, overflow: 'auto', padding: theme.spacing(2), display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, alignContent: 'start' },
  mtile: { textAlign: 'left', border: '1px solid #e4dbcf', borderRadius: 12, background: '#fff', padding: 12, minHeight: 96, display: 'flex', flexDirection: 'column', gap: 6, cursor: 'pointer', '&:hover': { borderColor: RED } },
  mtileTop: { display: 'flex', alignItems: 'flex-start', gap: 8 },
  code: { fontFamily: 'monospace', fontWeight: 700, fontSize: 12, color: RED, background: '#f6e7e3', padding: '3px 7px', borderRadius: 6 },
  codeBar: { color: '#3c6ea0', background: '#e7eef5' },
  mname: { fontSize: 14, fontWeight: 700, lineHeight: 1.2 },
  mprice: { marginTop: 'auto', fontFamily: 'monospace', fontWeight: 600, color: '#6e6357' },
  ticket: { width: 340, flex: 'none', display: 'flex', flexDirection: 'column', background: '#f7f2eb' },
  thead: { display: 'flex', alignItems: 'center', padding: theme.spacing(2), borderBottom: '1px solid #e4dbcf' },
  ctabs: { display: 'flex', flexWrap: 'wrap', gap: 6, padding: theme.spacing(1, 1.5) },
  ctab: { minWidth: 0, borderRadius: 999, padding: '6px 14px', fontWeight: 700, border: '1px solid #e4dbcf', color: '#6e6357' },
  ctabOn: { background: RED, color: '#fff' },
  tlines: { flex: 1, overflow: 'auto', padding: theme.spacing(0, 1.5, 1.5) },
  tgroup: { background: '#fff', border: '1px solid #e4dbcf', borderRadius: 12, padding: 8, marginBottom: 9 },
  tgHead: { display: 'flex', alignItems: 'center', gap: 8, padding: '2px 4px 8px' },
  tline: { padding: '8px 4px' },
  tlineR: { display: 'flex', alignItems: 'center', gap: 8 },
  stepper: { display: 'flex', alignItems: 'center', border: '1px solid #e4dbcf', borderRadius: 8 },
  stepBtn: { padding: 4 },
  qn: { minWidth: 26, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 },
  grip: {
    cursor: 'grab',
    touchAction: 'none',
    color: '#c9bfae',
    fontSize: 20,
    lineHeight: 1,
    padding: '6px 4px',
    marginRight: 2,
    userSelect: 'none',
    flex: 'none',
  },
  tfoot: { borderTop: '1px solid #e4dbcf', padding: theme.spacing(2) },
  doneBtn: { width: '100%', minHeight: 52, background: RED, color: '#fff', fontWeight: 800, fontSize: 16, '&:hover': { background: '#8f2d24' } },

  askPill: {
    display: 'inline-block',
    marginTop: 4,
    padding: '2px 8px',
    borderRadius: 999,
    background: '#e7f1f7',
    color: '#0277bd',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.02em',
  },

  /* floor setup */
  setup: { maxWidth: 1100, margin: '0 auto', width: '100%' },
  tset: { display: 'flex', gap: 12, background: '#fff', border: '1px solid #e4dbcf', borderRadius: 14, padding: 14 },
}));

/** Short labels for the board card: the popup carries the full wording. */
const ASK_LABEL: { [k: string]: string } = {
  waiter: 'Waiter',
  cutlery: 'Cutlery',
  napkins: 'Napkins',
  water: 'Water',
  bill: 'Bill',
};

const statusOf = (bt: BoardTable): { label: string; color: string; wash: string } => {
  if (!bt.order) return { label: 'Free', color: '#9c9488', wash: '#eeeae3' };
  if (bt.order.needsSend) return { label: 'Not sent', color: '#c2872a', wash: '#f6edda' };
  // A table stays "cooking" across every course; the card says WHICH course
  // rather than flipping state each time one fires (Lucy 2026-09-06).
  if (bt.order.guestState === 'cooking') {
    const c = bt.order.currentCourse;
    return {
      label: c ? `Cooking · course ${c}` : 'Cooking',
      color: '#2f6f4f',
      wash: '#e6f2ea',
    };
  }
  if (bt.order.guestState === 'ordering') {
    return { label: 'Ordering', color: '#3c6ea0', wash: '#e7eef5' };
  }
  return { label: 'Open', color: '#3c6ea0', wash: '#e7eef5' };
};

/* --------------------------------------------------------------- component */

export const TableBoard: React.FC = () => {
  const classes = useStyles();
  const [view, setView] = React.useState<'board' | 'floor'>('board');
  const [board, setBoard] = React.useState<BoardTable[]>([]);
  const [menu, setMenu] = React.useState<PosMenuItem[]>([]);
  const [order, setOrder] = React.useState<TableOrder | null>(null);
  const [selectedTable, setSelectedTable] = React.useState<BoardTable | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [toast, setToast] = React.useState<string | null>(null);
  const [fullscreen, setFullscreen] = React.useState(false);
  const [posOpen, setPosOpen] = React.useState(false);
  const [payOpen, setPayOpen] = React.useState(false);

  React.useEffect(() => {
    loadBoard();
    loadMenu();
    const poll = window.setInterval(loadBoard, 30000);
    const onFocus = () => loadBoard();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(poll);
      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadBoard() {
    try {
      setBoard(await TableOrderService.getBoard());
    } catch (e) {
      setToast((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  async function loadMenu() {
    try {
      setMenu(await TableOrderService.getMenu());
    } catch (e) {
      /* menu load is not fatal to the board */
    }
  }

  async function selectTable(bt: BoardTable) {
    setSelectedTable(bt);
    if (!bt.order) {
      setOrder(null); // preview only — a tap must never open a table
      return;
    }
    setBusy(true);
    try {
      setOrder(await TableOrderService.getOrder(bt.order.id));
    } catch (e) {
      setToast((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function openTable(thenAddItems: boolean) {
    if (!selectedTable) return;
    setBusy(true);
    try {
      const o = await TableOrderService.open(selectedTable.id);
      setOrder(o);
      loadBoard();
      if (thenAddItems) setPosOpen(true);
    } catch (e) {
      setToast((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function refreshOrder(o: TableOrder) {
    setOrder(o);
    loadBoard();
  }

  async function fireCourse(course: number) {
    if (!order) return;
    setBusy(true);
    try {
      const res = await TableOrderService.fireCourse(order.id, course);
      setToast(res.printed ? `Course ${course} fired to the kitchen` : 'Nothing to fire');
      setOrder(await TableOrderService.getOrder(order.id));
      loadBoard();
    } catch (e) {
      setToast((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function sendBar() {
    if (!order) return;
    setBusy(true);
    try {
      const res = await TableOrderService.sendBar(order.id);
      setToast(res.printed ? 'Drinks sent to the bar' : 'No new drinks to send');
      setOrder(await TableOrderService.getOrder(order.id));
    } catch (e) {
      setToast((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  /**
   * The guest's bill, before they pay. Writes nothing: no receipt number is
   * allocated, no ledger row, and the table stays open. Cashing it is the
   * separate Pay & close step.
   */
  async function printPreBill() {
    if (!order) return;
    setBusy(true);
    try {
      await FiscalService.preBill(order.id, true);
      setToast(`Pre-bill printed for table ${order.restaurantTable?.label || ''}`);
    } catch (e) {
      setToast((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Cash the table. The payment method is asked for FIRST, because it is
   * printed on the fiscal receipt and cannot be corrected afterwards - the
   * receipt is immutable once issued.
   */
  async function payAndClose(paymentType: string) {
    if (!order) return;
    setBusy(true);
    setPayOpen(false);
    try {
      const res = await TableOrderService.close(order.id, paymentType);
      setToast(
        `Table ${order.restaurantTable?.label || ''} settled by ${paymentType} — ${money(orderTotal(order))}` +
          (res.receiptNo ? ` · receipt ${res.receiptNo}` : ''),
      );
      setOrder(null);
      setSelectedTable(null);
      loadBoard();
    } catch (e) {
      setToast((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function voidOrder() {
    if (!order) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Void this order and free the table? This cannot be undone.')) return;
    setBusy(true);
    try {
      await TableOrderService.voidOrder(order.id);
      setToast(`Table ${order.restaurantTable?.label || ''} voided and freed`);
      setOrder(null);
      setSelectedTable(null);
      loadBoard();
    } catch (e) {
      setToast((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  /**
   * A guest ordered from their phone. Approving turns their pending lines
   * into real ones and then sends them exactly as sendOrder does, so guest
   * food travels the same proven path as a waiter's.
   */
  async function approveGuestOrder() {
    if (!order) return;
    setBusy(true);
    try {
      const res = await TableOrderService.approveGuest(order.id);
      await TableOrderService.fireCourse(order.id, 1);
      await TableOrderService.sendBar(order.id);
      setOrder(await TableOrderService.getOrder(order.id));
      loadBoard();
      setToast(`Approved ${res.approved} guest item(s) and sent them`);
    } catch (e) {
      setToast((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function sendOrder() {
    if (!order) return;
    setBusy(true);
    try {
      await TableOrderService.fireCourse(order.id, 1);
      await TableOrderService.sendBar(order.id);
      setOrder(await TableOrderService.getOrder(order.id));
      loadBoard();
      setToast('Order sent — food to the kitchen, drinks to the bar');
    } catch (e) {
      setToast((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function toggleFullscreen() {
    const next = !fullscreen;
    setFullscreen(next);
    try {
      if (next && document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      } else if (!next && document.exitFullscreen && document.fullscreenElement) {
        document.exitFullscreen();
      }
    } catch (e) {
      /* fullscreen API is best-effort */
    }
  }

  return (
    <div className={fullscreen ? classes.full : classes.root}>
      <div className={classes.bar}>
        <Typography className={classes.title}>Peking · Table Service</Typography>
        <div className={classes.nav}>
          <Button
            className={`${classes.navBtn} ${view === 'board' ? classes.navOn : ''}`}
            onClick={() => setView('board')}
          >
            Service board
          </Button>
          <Button
            className={`${classes.navBtn} ${view === 'floor' ? classes.navOn : ''}`}
            onClick={() => setView('floor')}
          >
            Floor setup
          </Button>
        </div>
        <div className={classes.spacer} />
        <Tooltip title="Refresh">
          <IconButton onClick={loadBoard} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Button
          startIcon={fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          onClick={toggleFullscreen}
          style={{ fontWeight: 700 }}
        >
          {fullscreen ? 'Exit full screen' : 'Full screen'}
        </Button>
      </div>

      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </div>
      ) : view === 'floor' ? (
        <div className={classes.floorWrap}>
          <FloorSetup onToast={setToast} onChanged={loadBoard} />
        </div>
      ) : (
        <div className={classes.body}>
          <div className={classes.floorWrap}>
            {groupByArea(board).map((grp) => (
              <div key={grp.area}>
                <Typography className={classes.areaHead}>{grp.area}</Typography>
                <div className={classes.boardGrid}>
                  {grp.tables.map((bt) => {
                    const st = statusOf(bt);
                    return (
                      <div
                        key={bt.id}
                        className={`${classes.card} ${selectedTable && selectedTable.id === bt.id ? classes.cardSel : ''}`}
                        onClick={() => selectTable(bt)}
                      >
                        <div className={classes.cardBand} style={{ background: st.color }} />
                        <div className={classes.tnum}>
                          {bt.label} <span className={classes.tsub}>· {bt.seats}p</span>
                        </div>
                        <span className={classes.pill} style={{ background: st.wash, color: st.color }}>
                          {st.label}
                        </span>
                        {bt.order && <span className={classes.cardTot}>{money(bt.order.total)}</span>}
                        {/* What the table has asked for, so it is still
                            visible after the popup is snoozed. */}
                        {!!bt.order?.assistance?.length && (
                          <span className={classes.askPill}>
                            {bt.order.assistance.map((a) => ASK_LABEL[a] || a).join(' · ')}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className={classes.panelWrap}>
            <div className={classes.panel}>
              {!selectedTable ? (
                <div className={classes.emptyPanel}>
                  <Typography>Pick a table on the left to open it or see its order.</Typography>
                </div>
              ) : !order ? (
                <div className={classes.emptyPanel}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                    <div className={classes.otitle}>Table {selectedTable.label}</div>
                    <div className={classes.osub}>
                      {selectedTable.area} · {selectedTable.seats} seats · Free
                    </div>
                    <Button
                      className={classes.payBtn}
                      style={{ background: '#3c6ea0', flex: 'none', width: 240 }}
                      onClick={() => openTable(false)}
                      disabled={busy}
                    >
                      Open table
                    </Button>
                    <Button
                      className={classes.addBtn}
                      style={{ flex: 'none', width: 240 }}
                      startIcon={<AddIcon />}
                      onClick={() => openTable(true)}
                      disabled={busy}
                    >
                      Open &amp; take order
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className={classes.ohead}>
                    <div>
                      <div className={classes.otitle}>Table {order.restaurantTable?.label}</div>
                      <div className={classes.osub}>
                        {order.restaurantTable?.area} · {order.restaurantTable?.seats} seats
                      </div>
                    </div>
                    <div style={{ flex: 1 }} />
                    <Button
                      onClick={voidOrder}
                      disabled={busy}
                      style={{ color: '#d2542e', fontWeight: 700, alignSelf: 'flex-start' }}
                    >
                      Void
                    </Button>
                  </div>

                  {courseNumbers(order).map((c) => {
                    const items = foodItems(order).filter((i) => (i.course || 1) === c);
                    const fired = items.length > 0 && items.every((i) => i.status !== 'ordered');
                    return (
                      <Paper key={c} elevation={0} className={classes.course}>
                        <div className={classes.chead}>
                          <span className={classes.cname}>Course {c}</span>
                          <span
                            className={classes.cstat}
                            style={fired ? { background: '#f6edda', color: '#be8324' } : { background: '#e7eef5', color: '#3c6ea0' }}
                          >
                            {fired ? 'Fired' : 'Ordered'}
                          </span>
                          {c > 1 && (
                            <Button
                              className={classes.fireBtn}
                              disabled={busy || !items.some((i) => i.status === 'ordered')}
                              onClick={() => fireCourse(c)}
                            >
                              {fired ? '✓ Fired' : `Fire course ${c}`}
                            </Button>
                          )}
                        </div>
                        {items.map((i) => (
                          <div key={i.id} className={classes.itemRow}>
                            <span className={classes.itemQty}>{i.quantity}×</span>
                            <span className={classes.itemName}>
                              {i.title}
                              {i.notes && <span className={classes.itemNote}>▸ {i.notes}</span>}
                            </span>
                            <span className={classes.itemPrice}>{money(Number(i.unitPrice) * i.quantity)}</span>
                          </div>
                        ))}
                      </Paper>
                    );
                  })}

                  {drinkItems(order).length > 0 && (
                    <Paper elevation={0} className={classes.course} style={{ borderStyle: 'dashed' }}>
                      <div className={classes.chead}>
                        <span className={classes.cname}>Drinks</span>
                        <span className={classes.cstat} style={{ background: '#eeeae3', color: '#6e6357' }}>→ Bar</span>
                        {/*
                          No separate "Send to bar": Send order already sends
                          the drinks with everything else, and two buttons only
                          invited half-sent orders (Lucy 2026-09-06).
                        */}
                        {drinkItems(order).some((i) => i.status === 'ordered') && (
                          <span className={classes.cstat} style={{ background: '#f6edda', color: '#8a6420' }}>
                            not sent
                          </span>
                        )}
                      </div>
                      {drinkItems(order).map((i) => (
                        <div key={i.id} className={classes.itemRow}>
                          <span className={classes.itemQty} style={{ color: '#3c6ea0' }}>{i.quantity}×</span>
                          <span className={classes.itemName}>
                            {i.title}
                            {i.notes && <span className={classes.itemNote}>▸ {i.notes}</span>}
                          </span>
                          <span className={classes.itemPrice}>{money(Number(i.unitPrice) * i.quantity)}</span>
                        </div>
                      ))}
                    </Paper>
                  )}

                  <div className={classes.totRow}>
                    <Typography style={{ fontWeight: 700, color: '#6e6357' }}>Order total</Typography>
                    <span className={classes.totAmt}>{money(orderTotal(order))}</span>
                  </div>
                </>
              )}
            </div>
            {order && (
              <div className={classes.actions} style={{ flexWrap: 'wrap' }}>
                {(order.tableOrderItems || []).some((i) => i.status === 'pending') && (
                  <Button
                    onClick={approveGuestOrder}
                    disabled={busy}
                    style={{
                      flexBasis: '100%',
                      minHeight: 52,
                      fontWeight: 800,
                      fontSize: 15,
                      background: '#a3241c',
                      color: '#fff',
                    }}
                  >
                    Approve guest order &amp; send to kitchen
                  </Button>
                )}
                {orderNeedsSend(order) && (
                  <Button
                    onClick={sendOrder}
                    disabled={busy}
                    style={{
                      flexBasis: '100%',
                      minHeight: 52,
                      fontWeight: 800,
                      fontSize: 15,
                      background: '#c2872a',
                      color: '#fff',
                    }}
                  >
                    Send order to kitchen
                  </Button>
                )}
                <Button className={classes.addBtn} startIcon={<AddIcon />} onClick={() => setPosOpen(true)}>
                  {(order.tableOrderItems || []).length ? 'Continue order' : 'Add items'}
                </Button>
                <Button
                  className={classes.addBtn}
                  startIcon={<ReceiptIcon />}
                  onClick={printPreBill}
                  disabled={busy || !(order.tableOrderItems || []).length}
                >
                  Pre-bill
                </Button>
                <Button
                  className={classes.payBtn}
                  onClick={() => setPayOpen(true)}
                  disabled={busy}
                >
                  Pay &amp; close
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Asked BEFORE the sale is cashed: the method is printed on the fiscal
          receipt and a receipt cannot be altered once issued. */}
      <Dialog open={payOpen} onClose={() => setPayOpen(false)}>
        <DialogTitle>How is the table paying?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: 12 }}>
            {order ? `Table ${order.restaurantTable?.label || ''} — ${money(orderTotal(order))}` : ''}
          </Typography>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[PaymentType.CASH, PaymentType.CARD, PaymentType.REVOLUT].map((pt) => (
              <Button
                key={pt}
                variant="contained"
                color="primary"
                size="large"
                disabled={busy}
                onClick={() => payAndClose(PaymentTypeLabel[pt])}
              >
                {PaymentTypeLabel[pt]}
              </Button>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {order && (
        <Pos
          open={posOpen}
          order={order}
          menu={menu}
          onClose={() => setPosOpen(false)}
          onChanged={refreshOrder}
          onToast={setToast}
        />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={2600}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </div>
  );
};

/* ------------------------------------------------------------------- POS */

/** A course group that dishes can be dropped into. */
const DroppableCourse: React.FC<{ course: number; children: React.ReactNode }> = ({
  course,
  children,
}) => {
  const classes = useStyles();
  const { setNodeRef, isOver } = useDroppable({ id: `course-${course}` });
  return (
    <div
      ref={setNodeRef}
      className={classes.tgroup}
      style={isOver ? { outline: `2px dashed ${RED}`, outlineOffset: 2 } : undefined}
    >
      {children}
    </div>
  );
};

/** One draggable dish row (drag by the grip handle so steppers/notes still work). */
const DragItem: React.FC<{
  item: TableOrderItem;
  onStep: (it: TableOrderItem, d: number) => void;
  onNote: (it: TableOrderItem, v: string) => void;
}> = ({ item, onStep, onNote }) => {
  const classes = useStyles();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: item.id });
  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.4 : 1,
    position: 'relative',
    zIndex: isDragging ? 20 : undefined,
    background: isDragging ? '#fff' : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} className={classes.tline}>
      <div className={classes.tlineR}>
        <span className={classes.grip} title="Drag to another course" {...listeners} {...attributes}>
          ⠿
        </span>
        <div className={classes.stepper}>
          <IconButton className={classes.stepBtn} size="small" onClick={() => onStep(item, -1)}>
            <RemoveIcon fontSize="small" />
          </IconButton>
          <span className={classes.qn}>{item.quantity}</span>
          <IconButton className={classes.stepBtn} size="small" onClick={() => onStep(item, 1)}>
            <AddIcon fontSize="small" />
          </IconButton>
        </div>
        <span className={classes.itemName} style={{ fontSize: 14 }}>{item.title}</span>
        <span className={classes.itemPrice}>{money(Number(item.unitPrice) * item.quantity)}</span>
      </div>
      <TextField
        fullWidth
        placeholder="＋ note / modifier (no chilli…)"
        defaultValue={item.notes || ''}
        onBlur={(e) => onNote(item, e.target.value)}
        InputProps={{ style: { fontSize: 13 } }}
        style={{ marginTop: 6 }}
      />
    </div>
  );
};

interface PosProps {
  open: boolean;
  order: TableOrder;
  menu: PosMenuItem[];
  onClose: () => void;
  onChanged: (o: TableOrder) => void;
  onToast: (m: string) => void;
}

const Pos: React.FC<PosProps> = ({ open, order, menu, onClose, onChanged, onToast }) => {
  const classes = useStyles();
  const [q, setQ] = React.useState('');
  const [cat, setCat] = React.useState('All');
  const [activeCourse, setActiveCourse] = React.useState(1);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setQ('');
      setCat('All');
      const cs = courseNumbers(order);
      setActiveCourse(cs.length ? cs[0] : 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over) return;
    const targetCourse = parseInt(String(over.id).replace('course-', ''), 10);
    const it = foodItems(order).find((x) => x.id === active.id);
    if (it && targetCourse && (it.course || 1) !== targetCourse) {
      move(it, targetCourse);
    }
  }

  const sections = ['All', ...Array.from(new Set(menu.map((m) => m.section || 'Other')))];
  const list = menu.filter((m) => {
    if (m.soldOut) return false;
    if (q) {
      const s = q.toLowerCase();
      return (
        (m.code || '').toLowerCase().indexOf(s) === 0 ||
        m.title.toLowerCase().indexOf(s) >= 0
      );
    }
    return cat === 'All' || (m.section || 'Other') === cat;
  });

  async function add(m: PosMenuItem) {
    setBusy(true);
    try {
      const o = await TableOrderService.addItem(order.id, m.id, 1, activeCourse);
      onChanged(o);
      setQ('');
    } catch (e) {
      onToast((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function step(it: TableOrderItem, delta: number) {
    setBusy(true);
    try {
      const nextQty = it.quantity + delta;
      const o =
        nextQty <= 0
          ? await TableOrderService.removeItem(it.id)
          : await TableOrderService.updateItem(it.id, { quantity: nextQty });
      onChanged(o);
    } catch (e) {
      onToast((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function move(it: TableOrderItem, course: number) {
    setBusy(true);
    try {
      onChanged(await TableOrderService.moveItem(it.id, course));
    } catch (e) {
      onToast((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function note(it: TableOrderItem, value: string) {
    try {
      onChanged(await TableOrderService.updateItem(it.id, { notes: value }));
    } catch (e) {
      onToast((e as Error).message);
    }
  }
  async function send() {
    setBusy(true);
    try {
      // On submission, Course 1 fires to the kitchen on its own and drinks go
      // to the bar. Later courses stay held for manual firing on the board.
      await TableOrderService.fireCourse(order.id, 1);
      await TableOrderService.sendBar(order.id);
      onChanged(await TableOrderService.getOrder(order.id));
      onToast('Order sent — Course 1 to the kitchen, drinks to the bar');
      onClose();
    } catch (e) {
      onToast((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function removeCourseFn(c: number) {
    const hasItems = foodItems(order).some((i) => (i.course || 1) === c);
    if (hasItems) {
      setBusy(true);
      try {
        onChanged(await TableOrderService.removeCourse(order.id, c));
      } catch (e) {
        onToast((e as Error).message);
      } finally {
        setBusy(false);
      }
    }
    setActiveCourse(1);
  }

  const foods = courseNumbers(order);
  const courseTabs = Array.from(new Set<number>([1, ...foods, activeCourse])).sort((a, b) => a - b);
  const drinks = drinkItems(order);

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <div className={classes.pos}>
        <div className={classes.posMenu}>
          <div className={classes.posSearch}>
            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              placeholder="Type a number like 61, or a dish name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <div className={classes.cats}>
              {sections.map((s) => (
                <Button
                  key={s}
                  className={`${classes.ctab} ${cat === s && !q ? classes.ctabOn : ''}`}
                  onClick={() => {
                    setCat(s);
                    setQ('');
                  }}
                >
                  {s}
                </Button>
              ))}
            </div>
          </div>
          <div className={classes.mgrid}>
            {list.map((m) => (
              <div key={m.id} className={classes.mtile} onClick={() => !busy && add(m)}>
                <div className={classes.mtileTop}>
                  {m.code ? (
                    <span className={`${classes.code} ${m.target === 'bar' ? classes.codeBar : ''}`}>{m.code}</span>
                  ) : m.target === 'bar' ? (
                    <span className={`${classes.code} ${classes.codeBar}`}>bar</span>
                  ) : null}
                  <span className={classes.mname}>{m.title}</span>
                </div>
                <span className={classes.mprice}>{money(m.price)}</span>
              </div>
            ))}
            {list.length === 0 && (
              <Typography style={{ gridColumn: '1/-1', textAlign: 'center', color: '#9a8e80', padding: 40 }}>
                Nothing matches that. Try a number or a name.
              </Typography>
            )}
          </div>
        </div>

        <div className={classes.ticket}>
          <div className={classes.thead}>
            <Typography style={{ fontWeight: 800, fontSize: 18 }}>
              Table {order.restaurantTable?.label}
            </Typography>
            <div style={{ flex: 1 }} />
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </div>
          <div className={classes.ctabs}>
            {courseTabs.map((c) => (
              <Chip
                key={c}
                label={`Course ${c}`}
                onClick={() => setActiveCourse(c)}
                onDelete={c > 1 ? () => removeCourseFn(c) : undefined}
                style={{
                  fontWeight: 700,
                  marginRight: 6,
                  ...(activeCourse === c ? { background: RED, color: '#fff' } : {}),
                }}
              />
            ))}
            <Button
              className={classes.ctab}
              style={{ borderStyle: 'dashed', color: RED }}
              onClick={() => setActiveCourse(Math.max(1, ...courseTabs) + 1)}
            >
              ＋ course
            </Button>
          </div>
          <div className={classes.tlines}>
            <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={onDragEnd}>
              {foods.map((c) => {
                const items = foodItems(order).filter((i) => (i.course || 1) === c);
                if (!items.length) return null;
                return (
                  <DroppableCourse key={c} course={c}>
                    <div className={classes.tgHead}>
                      <span className={classes.cname}>Course {c}</span>
                    </div>
                    {items.map((it) => (
                      <DragItem key={it.id} item={it} onStep={step} onNote={note} />
                    ))}
                  </DroppableCourse>
                );
              })}
            </DndContext>
            {drinks.length > 0 && (
              <div className={classes.tgroup} style={{ borderStyle: 'dashed' }}>
                <div className={classes.tgHead}>
                  <span className={classes.cname}>Drinks → Bar</span>
                </div>
                {drinks.map((it) => (
                  <div key={it.id} className={classes.tline}>
                    <div className={classes.tlineR}>
                      <div className={classes.stepper}>
                        <IconButton className={classes.stepBtn} size="small" onClick={() => step(it, -1)}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <span className={classes.qn}>{it.quantity}</span>
                        <IconButton className={classes.stepBtn} size="small" onClick={() => step(it, 1)}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </div>
                      <span className={classes.itemName} style={{ fontSize: 14 }}>{it.title}</span>
                      <span className={classes.itemPrice}>{money(Number(it.unitPrice) * it.quantity)}</span>
                    </div>
                    <TextField
                      fullWidth
                      placeholder="＋ note / modifier (lemonade…)"
                      defaultValue={it.notes || ''}
                      onBlur={(e) => note(it, e.target.value)}
                      InputProps={{ style: { fontSize: 13 } }}
                      style={{ marginTop: 6 }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className={classes.tfoot}>
            <div className={classes.totRow} style={{ marginTop: 0, borderTop: 'none', paddingTop: 0 }}>
              <Typography style={{ fontWeight: 700, color: '#6e6357' }}>Order total</Typography>
              <span className={classes.totAmt}>{money(orderTotal(order))}</span>
            </div>
            <Button className={classes.doneBtn} onClick={send} disabled={busy} style={{ marginTop: 10 }}>
              Send order
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

/* ---------------------------------------------------------------- floor */

interface FloorProps {
  onToast: (m: string) => void;
  onChanged: () => void;
}

const FloorSetup: React.FC<FloorProps> = ({ onToast, onChanged }) => {
  const classes = useStyles();
  const [rows, setRows] = React.useState<import('interfaces').RestaurantTable[]>([]);
  // The printable card that goes on the table. Its QR encodes this table's
  // own token, which is how a scan knows which table it is (2026-09-06).
  const [qrTable, setQrTable] =
    React.useState<import('interfaces').RestaurantTable | null>(null);

  React.useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function load() {
    try {
      setRows(await TableOrderService.getTables());
    } catch (e) {
      onToast((e as Error).message);
    }
  }
  async function save(t: import('interfaces').RestaurantTable) {
    try {
      await TableOrderService.saveTable(t);
      onChanged();
    } catch (e) {
      onToast((e as Error).message);
    }
  }
  async function addTable() {
    try {
      await TableOrderService.saveTable({ label: String(rows.length + 1), area: 'Main room', seats: 2, isActive: true });
      onToast('Table added');
      load();
      onChanged();
    } catch (e) {
      onToast((e as Error).message);
    }
  }
  async function remove(id: number) {
    try {
      await TableOrderService.deleteTable(id);
      load();
      onChanged();
    } catch (e) {
      onToast((e as Error).message);
    }
  }

  return (
    <div className={classes.setup}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <Typography variant="h6" style={{ fontWeight: 800 }}>Your tables</Typography>
          <Typography style={{ color: '#6e6357', fontSize: 13 }}>
            Add, name and arrange your tables. Each one gets a QR code for guests to scan.
          </Typography>
        </div>
        <div style={{ flex: 1 }} />
        <Button className={classes.addBtn} style={{ flex: 'none' }} startIcon={<AddIcon />} onClick={addTable}>
          Add table
        </Button>
      </div>
      <div className={classes.grid}>
        {rows.map((t) => (
          <div key={t.id} className={classes.tset}>
            <div style={{ flex: 1 }}>
              <TextField
                label="Number"
                defaultValue={t.label}
                onBlur={(e) => save({ ...t, label: e.target.value })}
              />
              <TextField
                label="Area"
                defaultValue={t.area || ''}
                onBlur={(e) => save({ ...t, area: e.target.value })}
                style={{ marginLeft: 8, width: 110 }}
              />
              <TextField
                label="Seats"
                type="number"
                defaultValue={t.seats}
                onBlur={(e) => save({ ...t, seats: Number(e.target.value) || 2 })}
                style={{ marginTop: 8, width: 80 }}
              />
              <div style={{ marginTop: 8 }}>
                <Button size="small" onClick={() => setQrTable(t)}>⎙ Print QR</Button>
                <Button size="small" style={{ color: '#d2542e' }} onClick={() => remove(t.id)}>Remove</Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <TableQrDialog table={qrTable} onClose={() => setQrTable(null)} />
    </div>
  );
};

/* --------------------------------------------------------------- utility */

function groupByArea(board: BoardTable[]): { area: string; tables: BoardTable[] }[] {
  const map: { [area: string]: BoardTable[] } = {};
  board.forEach((t) => {
    const a = t.area || 'Main room';
    if (!map[a]) map[a] = [];
    map[a].push(t);
  });
  return Object.keys(map).map((area) => ({ area, tables: map[area] }));
}
